<template>
  <div class="asv-page">

    <!-- Not Found Dialog -->
    <v-dialog v-model="notFoundDialog" max-width="400" persistent>
      <v-card class="pa-2">
        <v-card-title class="warning--text subheading font-weight-bold">Student Not Found</v-card-title>
        <v-card-text>We could not find this student record. It may have been deleted.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" dark @click="$router.push('/alumini')">Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading -->
    <v-container v-if="loading" fill-height fluid>
      <v-layout align-center justify-center column>
        <v-progress-circular indeterminate color="success" size="56"></v-progress-circular>
        <div class="mt-3 subheading grey--text">Loading...</div>
      </v-layout>
    </v-container>

    <!-- Dashboard -->
    <div v-if="!loading && alumini" class="asv-dashboard">

      <!-- ══ HEADER ══ -->
      <div class="asv-header">

        <!-- Main header body -->
        <div class="asv-header-body">

          <!-- Left: identity -->
          <div class="asv-header-identity">
            <span class="asv-back-link" @click="$router.push('/alumini')">
              <v-icon small class="asv-back-icon">mdi-chevron-left</v-icon>Alumni List
            </span>
            <div class="asv-name-line">
              <span class="asv-name">{{ aluminiPrimaryName }}</span>
              <div v-if="loadingChildren" class="asv-status-badge badge-loading">
                <v-progress-circular indeterminate size="10" width="2" color="white" class="mr-1"></v-progress-circular>
                Checking...
              </div>
              <template v-else>
                <div class="asv-status-badge" :class="currentMapping ? 'badge-mapped' : 'badge-unmapped'">
                  <v-icon x-small class="mr-1" color="white">{{ currentMapping ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}</v-icon>
                  {{ currentMapping ? 'Mapped' : 'Not Mapped' }}
                </div>
                <v-btn v-if="!currentMapping" color="success" small @click="showMapPanel = !showMapPanel" class="ma-0">
                  <v-icon left small>mdi-link-variant</v-icon>Map to Family
                </v-btn>
              </template>
            </div>
            <div v-if="aluminiSecondaryName" class="asv-name-eng">
              {{ aluminiSecondaryName }}
            </div>
            <!-- Detail chips row -->
            <div class="asv-chips-row">
              <span v-if="alumini.class"       class="asv-chip"><span class="asv-chip-lbl">Class</span>{{ alumini.class }}</span>
              <span v-if="alumini.section"     class="asv-chip"><span class="asv-chip-lbl">Section</span>{{ alumini.section }}</span>
              <span v-if="alumini.regID"       class="asv-chip"><span class="asv-chip-lbl">Last Term</span>{{ alumini.regID }}</span>
              <span v-if="alumini.branch"      class="asv-chip"><span class="asv-chip-lbl">Branch</span>{{ alumini.branch }}</span>
              <span v-if="alumini.dob"         class="asv-chip"><span class="asv-chip-lbl">DOB</span>{{ alumini.dob | formatDate }}</span>
              <span v-if="alumini.weddingYear" class="asv-chip"><span class="asv-chip-lbl">Wed.</span>{{ alumini.weddingYear }}</span>
            </div>
          </div>

        </div>

        <!-- Mapping info bar -->
        <div v-if="!loadingChildren && currentMapping" class="asv-mapping-bar">
          <v-icon color="success" small class="mr-1">mdi-check-circle</v-icon>
          <span>Mapped as <strong>{{ currentMapping.mappingType }}</strong> to <strong>{{ currentMapping.childDisplayName }}</strong></span>
          <v-btn flat x-small color="error" class="my-0 ml-3" @click="openDeleteMappingDialog(currentMapping)">
            <v-icon left x-small>mdi-link-off</v-icon>Remove
          </v-btn>
        </div>

        <!-- Map panel -->
        <v-slide-y-transition>
          <div v-if="showMapPanel && !currentMapping" class="asv-map-panel">
            <span class="body-2 asv-map-label">Map as <strong>{{ mappingType }}</strong> to:</span>
            <v-select
              v-model="selectedChild"
              :items="mappableChildren"
              item-text="displayName"
              item-disabled="disabled"
              return-object
              label="Choose child..."
              solo
              dense
              hide-details
              clearable
              no-data-text="No children available"
              class="asv-map-select"
            >
              <template v-slot:item="data">
                <template>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ data.item.displayName }}</v-list-tile-title>
                    <v-list-tile-sub-title v-if="data.item.mappingStatus">{{ data.item.mappingStatus }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </template>
              </template>
            </v-select>
            <v-btn flat small @click="showMapPanel = false; selectedChild = null" class="ma-0">Cancel</v-btn>
            <v-btn color="success" small :loading="mapping" :disabled="!selectedChild" @click="saveMapping" class="ma-0">Save</v-btn>
          </div>
        </v-slide-y-transition>
      </div>

      <!-- ══ THREE PANELS ══ -->
      <div class="asv-panels">

        <!-- Panel 1: Parent Info -->
        <div class="asv-panel">
          <div class="asv-panel-title">Parent Information</div>
          <v-progress-linear v-if="loadingDonor" indeterminate color="success" class="mb-2"></v-progress-linear>
          <div v-if="!loadingDonor && donor">
            <div class="asv-info-name">{{ donor.nameH }}</div>
            <div class="asv-info-row" v-if="donor.personID">
              <span class="asv-info-lbl">Person ID</span>
              <span class="asv-info-val">{{ donor.personID }}</span>
            </div>
            <div class="asv-info-row" v-if="donor.father">
              <span class="asv-info-lbl">Father</span>
              <span class="asv-info-val">{{ donor.father }}</span>
            </div>
            <div class="asv-info-row" v-if="donor.fatherInLaw">
              <span class="asv-info-lbl">Father In Law</span>
              <span class="asv-info-val">{{ donor.fatherInLaw }}</span>
            </div>
            <div class="asv-info-row" v-if="donor.address1">
              <span class="asv-info-lbl">Address</span>
              <span class="asv-info-val">{{ donor.address1 }}</span>
            </div>
            <div class="asv-info-row" v-if="donor.emailAddress">
              <span class="asv-info-lbl">Email</span>
              <span class="asv-info-val">{{ donor.emailAddress }}</span>
            </div>
            <div class="asv-info-row" v-for="phone in phones" :key="phone.phoneNum">
              <span class="asv-info-lbl">{{ getPhoneType(phone.phoneType) }}</span>
              <span class="asv-info-val">{{ phone.phoneNum }}</span>
            </div>
          </div>
          <div v-if="!loadingDonor && !donor" class="grey--text">No parent info.</div>
        </div>

        <!-- Panel 2: Married Children -->
        <div class="asv-panel">
          <div class="asv-panel-title">
            Married Children <span class="asv-count">{{ allChildren.length }}</span>
          </div>
          <v-progress-linear v-if="loadingChildren" indeterminate color="success" class="mb-2"></v-progress-linear>
          <div v-if="!loadingChildren">
            <div v-if="!allChildren.length" class="grey--text">No married children.</div>
            <div v-for="(child, idx) in allChildren" :key="child.personID">
              <div class="asv-child-row">
                <v-icon small class="mr-2" style="margin-top:2px; flex-shrink:0;"
                  :color="child.aluminiPersons && child.aluminiPersons.length ? 'success' : '#ccc'">
                  {{ child.aluminiPersons && child.aluminiPersons.length ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
                <div style="flex:1; min-width:0;">
                  <span class="asv-child-name">{{ child.displayName }}</span>
                  <div v-if="child.aluminiPersons && child.aluminiPersons.length" class="asv-child-mappings">
                    <div v-for="mapping in getChildAluminiMappings(child)" :key="mapping.id" class="asv-child-mapping">
                      <span class="asv-mapping-type-tag">{{ mapping.mappingType }}</span>
                      <span class="asv-mapping-name">{{ mapping.studentDisplayName }}</span>
                      <v-btn flat icon small color="grey" class="my-0 ml-1" @click.stop="openDeleteMappingDialog(mapping)">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
              <v-divider v-if="idx + 1 < allChildren.length" class="my-2"></v-divider>
            </div>
          </div>
        </div>

        <!-- Panel 3: Alumini Students -->
        <div class="asv-panel">
          <div class="asv-panel-title">
            Alumini Students <span class="asv-count asv-count-blue">{{ aluminiStudents.length }}</span>
          </div>
          <v-progress-linear v-if="loadingAlumini" indeterminate color="success" class="mb-2"></v-progress-linear>
          <div v-if="!loadingAlumini">
            <div v-if="!aluminiStudents.length" class="grey--text">No alumini students.</div>
            <div v-for="(s, i) in aluminiStudents" :key="'al-' + s.studentID">
              <div class="asv-student-row">
                <div class="asv-student-dot" :class="s.disabled ? 'asv-dot-mapped' : 'asv-dot-alumini'"></div>
                <div style="flex:1;">
                  <div class="asv-student-name">
                    {{ (s.firstHebrew && s.firstHebrew.trim()) || (s.engName && s.engName.trim()) }} {{ (s.lastHebrew && s.lastHebrew.trim()) || (s.lastEnglish && s.lastEnglish.trim()) }}
                    <span v-if="alumini && s.studentID == alumini.studentID" class="asv-this-tag">★ Selected</span>
                  </div>
                  <div class="asv-student-sub">
                    {{ s.class }}<span v-if="s.regID"> · {{ s.regID }}</span>
                    <span v-if="s.disabled" class="asv-mapped-tag">mapped</span>
                  </div>
                </div>
              </div>
              <v-divider v-if="i + 1 < aluminiStudents.length" class="my-2"></v-divider>
            </div>
          </div>
        </div>

      </div><!-- /asv-panels -->
    </div><!-- /asv-dashboard -->

    <!-- REMOVE MAPPING -->
    <v-dialog v-model="deleteMappingDialog" max-width="400" persistent>
      <v-card class="pa-2">
        <v-card-title class="subheading font-weight-bold">Remove this mapping?</v-card-title>
        <v-card-text v-if="mappingToDelete">
          <div class="body-1"><strong>{{ mappingToDelete.mappingType }}</strong> mapped to {{ mappingToDelete.studentDisplayName }}</div>
          <div class="body-2 grey--text mt-1">Family: {{ mappingToDelete.childDisplayName }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="deleteMappingDialog = false">Cancel</v-btn>
          <v-btn color="success" :loading="processing" @click="deleteMapping">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
export default {
  data: () => ({
    loading: true,
    loadingDonor: false,
    loadingChildren: false,
    loadingAlumini: false,
    notFoundDialog: false,
    deleteMappingDialog: false,
    showMapPanel: false,
    alumini: null,
    donor: null,
    phones: [],
    allChildren: [],
    aluminiStudents: [],
    selectedChild: null,
    mappingToDelete: null,
    currentMapping: null,
    mapping: false,
    processing: false,
  }),
  computed: {
    aluminiPrimaryName() {
      if (!this.alumini) return "";
      const heb = [this.alumini.firstHebrew, this.alumini.lastHebrew].filter((v) => !!v && v.trim()).join(" ");
      if (heb) return heb;
      return [this.alumini.engName, this.alumini.lastEnglish].filter((v) => !!v && v.trim()).join(" ");
    },
    aluminiSecondaryName() {
      if (!this.alumini) return "";
      const heb = [this.alumini.firstHebrew, this.alumini.lastHebrew].filter((v) => !!v && v.trim()).join(" ");
      if (!heb) return "";
      return [this.alumini.engName, this.alumini.lastEnglish].filter((v) => !!v && v.trim()).join(" ");
    },
    mappingType() {
      if (!this.alumini) return "";
      const cls = this.alumini.class || "";
      return cls.startsWith("B-") || cls.startsWith("Y-") ? "Father" : "Mother";
    },
    mappableChildren() {
      const type = this.mappingType;
      const filtered =
        type === "Father"
          ? this.allChildren.filter((c) => c.childType === "SON")
          : this.allChildren.filter((c) => c.childType === "SON IN LAW");
      return filtered.map((child) => ({
        ...child,
        disabled: this.isChildMappedForType(child, type),
        mappingStatus: this.getChildMappingStatus(child),
      }));
    },
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      const aluminiId = this.$route.params.id;
      const fatherPersonID = this.$route.query.fatherPersonID;
      if (!fatherPersonID) {
        this.notFoundDialog = true;
        this.loading = false;
        return;
      }
      await this.loadDonor(fatherPersonID);
      await this.loadAluminiRecord(aluminiId, fatherPersonID);
      if (!this.alumini) {
        this.notFoundDialog = true;
        this.loading = false;
        return;
      }
      this.loading = false;
      this.loadPhones(fatherPersonID);
      this.loadChildren(fatherPersonID);
      this.loadAluminiStudents(fatherPersonID);
    },

    async loadDonor(personID) {
      this.loadingDonor = true;
      try {
        this.donor = await this.$http.get("api/donors/getDonor?personID=" + encodeURIComponent(personID));
      } catch (e) {
        console.error("Error loading donor:", e);
      }
      this.loadingDonor = false;
    },

    async loadAluminiRecord(aluminiId, personID) {
      try {
        const byPerson = await this.$http.get("api/donors/GetAluminiByPersonID?personID=" + encodeURIComponent(personID));
        const combined = Array.isArray(byPerson) ? [...byPerson] : [];
        if (this.donor && this.donor.parentId) {
          const byParent = await this.$http.get("api/donors/GetAlumini?parentId=" + encodeURIComponent(this.donor.parentId));
          if (Array.isArray(byParent)) {
            byParent.forEach((a) => {
              if (!combined.find((x) => x.studentID === a.studentID)) combined.push(a);
            });
          }
        }
        this.alumini = combined.find((a) => String(a.id) === String(aluminiId)) || null;
      } catch (e) {
        console.error("Error loading alumini record:", e);
      }
    },

    async loadPhones(personID) {
      try {
        const phones = await this.$http.get("api/donors/GetPhones?personID=" + encodeURIComponent(personID));
        this.phones = Array.isArray(phones) ? phones : [];
      } catch (e) {
        console.error("Error loading phones:", e);
      }
    },

    async loadChildren(personID) {
      this.loadingChildren = true;
      try {
        const children = await this.$http.get("api/donors/GetChildren?personID=" + encodeURIComponent(personID));
        this.allChildren = (Array.isArray(children) ? children : []).map((child) => {
          const nameParts = [child.titleHebrew, child.firstHebrew, child.lastHebrew, child.suffixHebrew].filter((p) => !!p);
          const weddingYearText = child.weddingYear ? " (" + child.weddingYear + ")" : "";
          child.displayName = nameParts.join(" ") + weddingYearText;
          return child;
        });
        this.findCurrentMapping();
      } catch (e) {
        console.error("Error loading children:", e);
      }
      this.loadingChildren = false;
    },

    async loadAluminiStudents(personID) {
      this.loadingAlumini = true;
      try {
        const aluminiCombined = [];
        if (this.donor && this.donor.parentId) {
          const byParent = await this.$http.get("api/donors/GetAlumini?parentId=" + encodeURIComponent(this.donor.parentId));
          if (Array.isArray(byParent)) byParent.forEach((a) => aluminiCombined.push(a));
        }
        if (personID) {
          const byPerson = await this.$http.get("api/donors/GetAluminiByPersonID?personID=" + encodeURIComponent(personID));
          if (Array.isArray(byPerson)) {
            byPerson.forEach((a) => {
              if (!aluminiCombined.find((x) => x.studentID === a.studentID)) aluminiCombined.push(a);
            });
          }
        }
        this.aluminiStudents = aluminiCombined.map((a) => {
          const isMapped = this.allChildren.find((child) => {
            if (!child.aluminiPersons) return false;
            return !!child.aluminiPersons.find((c) => c.studentID == a.studentID);
          });
          return { ...a, disabled: !!isMapped };
        });
      } catch (e) {
        console.error("Error loading alumini students:", e);
      }
      this.loadingAlumini = false;
    },

    findCurrentMapping() {
      if (!this.alumini) { this.currentMapping = null; return; }
      const studentID = this.alumini.studentID;
      const child = this.allChildren.find((c) => {
        if (!c.aluminiPersons || !c.aluminiPersons.length) return false;
        return !!c.aluminiPersons.find((a) => a.studentID == studentID);
      });
      if (!child) { this.currentMapping = null; return; }
      const aluminiPerson = child.aluminiPersons.find((a) => a.studentID == studentID);
      const student = child.aluminiPersonStudents && child.aluminiPersonStudents.find((s) => s.studentID == studentID);
      this.currentMapping = {
        id: aluminiPerson.id,
        childDisplayName: child.displayName,
        mappingType: aluminiPerson.type,
        studentDisplayName: student
          ? student.firstHebrew + " " + student.lastHebrew + " (" + student.class + " | " + student.regID + ")"
          : "Student ID: " + studentID,
      };
    },

    getChildAluminiMappings(child) {
      if (!child.aluminiPersons || !child.aluminiPersons.length) return [];
      return child.aluminiPersons.map((aluminiPerson) => {
        const student = child.aluminiPersonStudents && child.aluminiPersonStudents.find((s) => s.studentID == aluminiPerson.studentID);
        return {
          id: aluminiPerson.id,
          childDisplayName: child.displayName,
          mappingType: aluminiPerson.type,
          studentDisplayName: student
            ? student.firstHebrew + " " + student.lastHebrew + " (" + student.class + " | " + student.regID + ")"
            : "Student ID: " + aluminiPerson.studentID,
          displayName: "● " + aluminiPerson.type + " Mapped" + (student ? " to " + student.firstHebrew + " " + student.lastHebrew + " (" + student.class + " | " + student.regID + ")" : ""),
        };
      });
    },

    getChildMappingStatus(child) {
      if (!child.aluminiPersons || !child.aluminiPersons.length) return "";
      return this.getChildAluminiMappings(child).map((m) => m.displayName.replace("● ", "")).join(" | ");
    },

    isChildMappedForType(child, type) {
      if (!child.aluminiPersons) return false;
      return !!child.aluminiPersons.find((a) => a.type == type);
    },

    openDeleteMappingDialog(mapping) {
      this.mappingToDelete = mapping;
      this.deleteMappingDialog = true;
    },

    async deleteMapping() {
      if (!this.mappingToDelete || !this.mappingToDelete.id) return;
      this.processing = true;
      try {
        await this.$http.post("api/donors/deleteAluminiPerson?id=" + this.mappingToDelete.id);
        this.$notify({ text: "Mapping removed", group: "notification", type: "success" });
        this.deleteMappingDialog = false;
        this.mappingToDelete = null;
        const fatherPersonID = this.$route.query.fatherPersonID;
        this.loadChildren(fatherPersonID);
        this.loadAluminiStudents(fatherPersonID);
      } catch (e) {
        console.error("Error deleting mapping:", e);
      }
      this.processing = false;
    },

    async saveMapping() {
      if (!this.selectedChild || !this.alumini) return;
      this.mapping = true;
      try {
        await this.$http.post("api/donors/SaveAluminiPerson", {
          personID: this.selectedChild.personID,
          studentID: this.alumini.studentID,
          type: this.mappingType,
          createdBy: this.$store.state.authentication.user,
        });
        this.$notify({ text: "Mapping saved", group: "notification", type: "success" });
        this.showMapPanel = false;
        this.selectedChild = null;
        const fatherPersonID = this.$route.query.fatherPersonID;
        this.loadChildren(fatherPersonID);
        this.loadAluminiStudents(fatherPersonID);
      } catch (e) {
        console.error("Error saving mapping:", e);
      }
      this.mapping = false;
    },

    getPhoneType(phoneType) {
      switch (phoneType) {
        case "PHON": return "Home Phone";
        case "CEL": return "Cellphone";
        case "MCEL": return "Mother Cellphone";
        default: return phoneType;
      }
    },
  },
  filters: {
    formatDate(value) {
      if (!value) return "";
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return (date.getMonth() + 1) + "/" + date.getDate() + "/" + date.getFullYear();
      } catch (e) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
/* ── PAGE ── */
.asv-page {
  background: #f4f5f7;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── DASHBOARD WRAPPER ── */
.asv-dashboard {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ── HEADER ── */
.asv-header {
  background: #fff;
  border-left: 4px solid #43a047;
  border-radius: 8px;
  margin: 10px 12px 0;
  padding: 14px 16px 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.09);
}

.asv-header-body {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.asv-header-identity {
  flex: 1;
  min-width: 0;
}

.asv-back-link {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #aaa;
  cursor: pointer;
  margin-bottom: 6px;
  transition: color 0.15s;
}

.asv-back-link:hover {
  color: #43a047;
}

.asv-back-icon {
  font-size: 17px !important;
  margin-right: 2px;
  color: inherit !important;
}

.asv-name-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.asv-name {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
}

.asv-name-eng {
  font-size: 15px;
  color: #888;
  margin-top: 3px;
  font-weight: 400;
}

.asv-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.badge-mapped   { background: #43a047; color: #fff; }
.badge-unmapped { background: #f57c00; color: #fff; }
.badge-loading  { background: #9e9e9e; color: #fff; }

/* Detail chips */
.asv-chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.asv-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f7f7f7;
  border: 1px solid #e4e4e4;
  border-radius: 6px;
  padding: 4px 11px;
  font-size: 13px;
  color: #2a2a2a;
}

.asv-chip-lbl {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #bbb;
  letter-spacing: 0.5px;
}

/* Mapping info bar */
.asv-mapping-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 14px;
  background: #f0faf0;
  border: 1px solid #c8e6c9;
  border-left: 4px solid #43a047;
  border-radius: 6px;
  font-size: 14px;
  color: #2e7d32;
  font-weight: 500;
}

/* Map panel inside header */
.asv-map-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9fbe7;
  border: 1px solid #c5e1a5;
  border-radius: 6px;
}

.asv-map-label {
  flex-shrink: 0;
}

.asv-map-select {
  flex: 0 1 380px;
  min-width: 180px;
}

/* ── PANELS ROW ── */
.asv-panels {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: 10px;
  padding: 10px 12px 12px;
  background: #f0f2f5;
}

.asv-panel {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  background: #fff;
  border-left: 4px solid #43a047;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.09);
  min-width: 0;
}

/* ── PANEL TITLES ── */
.asv-panel-title {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e8f5e9;
  display: flex;
  align-items: center;
}

.asv-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  padding: 0 6px;
  height: 18px;
  margin-left: 6px;
}

/* ── PARENT INFO ── */
.asv-info-name {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.asv-info-row {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.asv-info-row:last-child {
  border-bottom: none;
}

.asv-info-lbl {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #bbb;
  margin-bottom: 2px;
}

.asv-info-val {
  font-size: 15px;
  color: #222;
  line-height: 1.3;
}

/* ── CHILDREN ── */
.asv-child-row {
  display: flex;
  align-items: flex-start;
  padding: 3px 0;
}

.asv-child-name { font-size: 16px; font-weight: 500; color: #1a1a1a; }

.asv-child-mappings { margin-top: 3px; }

.asv-child-mapping {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  flex-wrap: wrap;
  gap: 3px;
}

.asv-mapping-type-tag {
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  flex-shrink: 0;
}

.asv-mapping-name { font-size: 13px; color: #555; }

/* ── ALUMINI STUDENTS ── */
.asv-count-blue { background: #e3f2fd; color: #0277bd; }

.asv-student-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.asv-student-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.asv-dot-alumini { background: #0288d1; }
.asv-dot-mapped  { background: #4caf50; }

.asv-student-name { font-size: 15px; font-weight: 500; color: #1a1a1a; }
.asv-student-sub  { font-size: 12px; color: #999; margin-top: 1px; }

.asv-this-tag {
  display: inline-block;
  background: #1565c0;
  color: #fff;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
  letter-spacing: 0.3px;
}

.asv-mapped-tag {
  display: inline-block;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  margin-left: 4px;
}
</style>
